// Core
import React from 'react';
import { useSelector } from 'react-redux';
import StatsLoader from '../../../components/StatsLoader';
import WidgetTotalHeader from '../../../components/WidgetTotalHeader';
import {
  GECKO_CONSTS,
  GeckoBtn,
  GeckoIcon,
  GeckoModalService,
  GeckoRow,
} from 'ui';
import { Chart } from 'react-google-charts';
import graphColours from '../../../../../constants/graphColours';
import StatsLogic from '../../../Logic/StatsLogic';
import WorkHoursModal from '../../../components/WorkHoursModal/WorkHoursModal';

const sumConversations = (conversationPerHour) =>
  conversationPerHour.reduce(
    (accumulator, stat) => accumulator + Number(stat.counter),
    0
  );

const ConversationPerHour = ({ id, filters }) => {
  const conversationPerHour = useSelector(
    (state) => state.statsRoute.conversationPerHour
  );
  const fetchingStats = useSelector(
    (state) => state.statsRoute.fetchingConversationPerHourStats
  );
  const workHours = useSelector((state) => state.statsRoute.workHours);

  const totalConversations = sumConversations(conversationPerHour);

  const header = [
    'date',
    'Started During Working Hours',
    'Started Out of Hours',
  ];
  const options = {
    isStacked: true,
    hAxis: {
      title: '',
      minValue: 24,
      slantedText: true,
      slantedTextAngle: 45,
    },
    vAxis: {
      title: 'Opened Conversations',
      format: '#,###',
      minValue: 0,
      maxValue: 4,
      gridlines: { count: -1 },
    },
    legend: 'none',
    tooltip: { isHtml: true },
    focusTarget: 'category',
    colors: [graphColours.primary, graphColours.tertiary],
  };
  const renderFirstTimeConfig = () => (
    <>
      <div className={'Work-Hours-Stat-First-Configuration-Row'}>
        <div>
          <h1>Set Up Required</h1>
        </div>
        <div>Please set up the working hours and days for this stat</div>
        <div className="SetUpButton">
          <GeckoBtn
            type={GECKO_CONSTS.BTN_TYPE_DEFAULT}
            name={
              <span>
                <GeckoIcon icon="far fa-clock" /> Set Working Hours
              </span>
            }
            handler={() =>
              new GeckoModalService().open(() => (
                <WorkHoursModal filters={filters} />
              ))
            }
          />
        </div>
      </div>
    </>
  );

  const renderGraph = () => {
    const data = StatsLogic.formatConversationPerHourStats(
      conversationPerHour,
      workHours
    );

    const graphData = [header, ...data];

    const totalInHoursConversations = data.reduce(
      (total, current) => total + current[1],
      0
    );

    const totalOutOfHoursConversations = data.reduce(
      (total, current) => total + current[2],
      0
    );

    function calculatePercentage(number, total) {
      if (total === 0) {
        return 0;
      }
      return Math.round((number / total) * 100);
    }

    const totalInHoursConversationsAsPercentage = calculatePercentage(
      totalInHoursConversations,
      totalConversations
    );

    const totalOutOfHoursConversationsAsPercentage = calculatePercentage(
      totalOutOfHoursConversations,
      totalConversations
    );

    const hoursBreakdown = () => {
      return (
        <>
          <GeckoRow>
            <div className="col">
              <div
                className="float-left value secondary"
                style={{ marginTop: '8px' }}
              >
                <div className="Work-Hours-Row">
                  <div
                    className="Work-Hours-Circle"
                    style={{
                      backgroundColor: graphColours.primary,
                    }}
                  ></div>
                  <span
                    aria-label={`${totalInHoursConversations} in hours
                    conversations. ${totalInHoursConversationsAsPercentage}% of total conversation`}
                  >
                    {totalInHoursConversations} (
                    {totalInHoursConversationsAsPercentage}%) in hours
                    conversations
                  </span>
                </div>
              </div>
            </div>
          </GeckoRow>
          <GeckoRow>
            <div className="col">
              <div className="float-left value secondary">
                <div className="Work-Hours-Row">
                  <div
                    className="Work-Hours-Circle"
                    style={{
                      backgroundColor: graphColours.tertiary,
                    }}
                  ></div>
                  <span
                    aria-label={`${totalOutOfHoursConversations} out of hours
                    conversations. ${totalOutOfHoursConversationsAsPercentage}% of total conversation`}
                  >
                    {totalOutOfHoursConversations} (
                    {totalOutOfHoursConversationsAsPercentage}%) out of hours
                    conversations
                  </span>
                </div>
              </div>
            </div>
          </GeckoRow>
        </>
      );
    };

    return (
      <>
        <GeckoRow>
          <div className="col">
            <WidgetTotalHeader
              primary={totalConversations}
              suffix={
                <span style={{ fontSize: '14px' }}>
                  {'Total New Conversations'}
                </span>
              }
              showSecondary={false}
            />
          </div>
        </GeckoRow>
        <GeckoRow>
          <div className="col">{hoursBreakdown()}</div>
        </GeckoRow>
        <GeckoRow>
          <div className="col">
            <Chart
              chartType="ColumnChart"
              data={graphData}
              options={options}
              graph_id={id}
              width="100%"
              height="330px"
            />
          </div>
        </GeckoRow>
      </>
    );
  };

  return (
    <div>
      <StatsLoader isLoading={fetchingStats}>
        {workHours ? renderGraph() : renderFirstTimeConfig()}
      </StatsLoader>
    </div>
  );
};

export default ConversationPerHour;
